import { useMemo } from 'react';
import { Alert, Button, Divider, Stack } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useFormContext, useFormState } from 'FrontRoyalReactHookForm';
import {
    EditContentItemLocale,
    EditorCard,
    EntityMetadataForm,
    SavePublishPanel,
    SavingOverlay,
    useUnsavedChangesWarning,
    validationMessages,
} from 'ContentItemEditor';
import PlaylistCoursesSection from './PlaylistCoursesSection';
import PlaylistHeaderFields from './PlaylistHeaderFields';
import { useEditorCourses } from './useEditorCourses';
import { playlistEditorPath } from 'Playlists';
import { type PlaylistEditorFormProps, type PlaylistFormValues } from './PlaylistEditor.types';

// oxlint-disable-next-line max-lines-per-function -- the page is one long form of independent panels
export default function PlaylistEditorForm(props: PlaylistEditorFormProps) {
    const {
        record,
        recordDirty,
        availableContentFamilyIdentifiers,
        linkablePlaylists,
        playlistsLoading,
        canPublish,
        saving,
        justSaved,
        deleting,
        uploadingImage,
        savingMetadata,
        publishState,
        onPublishStateChange,
        onRecordEdit,
        onSave,
        onDelete,
        onDuplicate,
        onSaveMetadata,
        onUploadImage,
        onRemoveImage,
        onUploadSeoImage,
        onGoBack,
    } = props;

    const { control, watch, setFieldValue } = useFormContext<PlaylistFormValues>();
    const { isDirty, isValid, errors } = useFormState({ control });

    const title = watch('title');
    const locale = watch('locale');

    // The form cannot see an image or a locale pack change, so both are folded in here. Without
    // that, leaving the page after only an image change would not warn.
    useUnsavedChangesWarning(isDirty || recordDirty);

    const { courses, coursesByLocalePackId, isLoading: coursesLoading } = useEditorCourses();

    const linkableItems = useMemo(
        () =>
            linkablePlaylists.map(playlist => ({
                id: playlist.id,
                title: playlist.title,
                tag: playlist.tag,
                locale: playlist.locale || 'en',
                localePackId: playlist.locale_pack?.id,
            })),
        [linkablePlaylists],
    );

    return (
        <Stack spacing={4} sx={{ paddingBottom: 6 }}>
            <Stack direction="row">
                <Button startIcon={<ArrowBack />} onClick={onGoBack}>
                    Back to Playlist List
                </Button>
            </Stack>

            <EditorCard title="Playlist info">
                <PlaylistHeaderFields
                    image={record.image}
                    relatedCohorts={record.related_cohorts}
                    familyPlaylistTitles={record.family_playlist_titles}
                    availableContentFamilyIdentifiers={availableContentFamilyIdentifiers}
                    uploadingImage={uploadingImage}
                    onUploadImage={onUploadImage}
                    onRemoveImage={onRemoveImage}
                />
            </EditorCard>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} sx={{ alignItems: 'flex-start' }}>
                <Stack sx={{ flex: 1.4, minWidth: 0, width: '100%' }}>
                    <EditorCard title="Courses">
                        <PlaylistCoursesSection
                            courses={courses}
                            coursesByLocalePackId={coursesByLocalePackId}
                            coursesLoading={coursesLoading}
                            assessmentEditingLocked={!!record.locale_pack?.assessment_editing_locked}
                        />
                    </EditorCard>
                </Stack>

                <Stack sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <EditorCard title="Translation Tools">
                        <EditContentItemLocale
                            itemId={record.id}
                            itemTitle={title}
                            itemLabel="playlist"
                            locale={locale}
                            localePack={record.locale_pack}
                            linkableItems={linkableItems}
                            loading={playlistsLoading}
                            // A playlist locale pack carries no access groups. Playlist.groupable
                            // is false on the Angular model.
                            groupable={false}
                            editorUrlFor={playlistId => playlistEditorPath({ playlistId })}
                            onChange={({ locale: newLocale, localePack }) => {
                                setFieldValue('locale', newLocale);
                                onRecordEdit({ locale: newLocale, locale_pack: localePack });
                            }}
                        />

                        <Divider />

                        <Button
                            name="create-localization"
                            variant="outlined"
                            color="warning"
                            disabled={saving}
                            onClick={() => onDuplicate({ hideIsTranslation: false })}
                            sx={{ alignSelf: 'flex-start' }}
                        >
                            Create new Localization
                        </Button>
                    </EditorCard>
                </Stack>
            </Stack>

            <EditorCard title="Save, Publish, & Delete Playlist">
                {!isValid && (
                    <Alert severity="warning">
                        <Stack spacing={0.5}>
                            <span>The playlist cannot be saved yet:</span>

                            {validationMessages(errors).map(message => (
                                <span key={message}>{message}</span>
                            ))}
                        </Stack>
                    </Alert>
                )}

                <SavePublishPanel
                    itemHeader="Playlist"
                    title={title}
                    modifiedAt={record.modified_at}
                    publishedAt={record.published_at}
                    canPublish={canPublish}
                    busy={saving || deleting}
                    justSaved={justSaved}
                    publishState={publishState}
                    onPublishStateChange={onPublishStateChange}
                    onSave={onSave}
                    onDelete={onDelete}
                    // Always a plain copy: this button never offers a translation.
                    extraActions={
                        <Button
                            name="duplicate-playlist"
                            variant="contained"
                            color="warning"
                            disabled={saving || deleting}
                            onClick={() => onDuplicate({ hideIsTranslation: true })}
                        >
                            Duplicate Playlist
                        </Button>
                    }
                    // Validity only. An admin may tick Publish and save without editing a field,
                    // which a dirty check would make impossible.
                    saveDisabled={!isValid}
                />
            </EditorCard>

            <EditorCard title="SEO Fields">
                <EntityMetadataForm
                    itemId={record.id}
                    urlPrefix="playlist"
                    itemLabel="playlist"
                    utmCampaign="share_playlist"
                    image={record.entity_metadata?.image}
                    uploadingImage={uploadingImage}
                    onUploadImage={onUploadSeoImage}
                    onRemoveImage={() => onRecordEdit({ entity_metadata: { ...record.entity_metadata!, image: null } })}
                    onSave={onSaveMetadata}
                    saving={savingMetadata}
                />
            </EditorCard>

            <SavingOverlay open={saving} />
        </Stack>
    );
}
